import { TREE_NODE_HEIGHT } from "@/components/ui/tree/tree-node";
import { selectAllCadModels } from "@/store/cad/cad-selectors";
import { selectActiveCadId, setActiveCad } from "@/store/cad/cad-slice";
import {
  selectIsControlPointSheetAlignment,
  selectWizardElementToAlignId,
  selectWizardReferenceElementId,
} from "@/store/modes/alignment-wizard-mode-selectors";
import {
  resetWizardReferenceElementId,
  setControlPointsAlignmentEnabled,
  setWizardRecommendedElementId,
  setWizardReferenceElementId,
  setWizardReferencePointCloudStreamId,
} from "@/store/modes/alignment-wizard-mode-slice";
import {
  useAppDispatch,
  useAppSelector,
  useAppStore,
} from "@/store/store-hooks";
import { TreeData } from "@/utils/tree-generation/tree-data";
import {
  AnchorIcon,
  FaroText,
  FaroTooltip,
  NoTranslate,
  grey,
} from "@faro-lotv/flat-ui";
import { useTranslate } from "@faro-lotv/foreign-observers";
import { assert } from "@faro-lotv/foundation";
import {
  IElementGenericImgSheet,
  isIElementAreaSection,
  isIElementGenericDataSession,
  isIElementGenericImgSheet,
  isIElementModel3dStream,
  isIElementSectionGeoslam,
} from "@faro-lotv/ielement-types";
import {
  selectAncestor,
  selectChildrenDepthFirst,
  selectIElement,
} from "@faro-lotv/project-source";
import { Stack } from "@mui/system";
import { Store } from "@reduxjs/toolkit";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type JSX,
} from "react";
import { NodeApi, Tree, TreeApi } from "react-arborist";
import { OpenMap } from "react-arborist/dist/module/state/open-slice";
import { useDragDropManager } from "react-dnd";
import { getValidReferenceCloud } from "./align-wizard-mode-scene";
import { AlignmentWizardReferenceTreeNode } from "./align-wizard-reference-tree-node";
import {
  selectReferenceAreas,
  selectReferencePointClouds,
} from "./reference-selectors";

export enum ReferenceTreeFolderIds {
  /** string to use as an ID of 3D models folder in references selector tree of Alignment Wizard   */
  modelFolderId = "Models references root folder",

  /** string to use as an ID of clouds folder in references selector tree of Alignment Wizard   */
  cloudsFolderId = "Clouds references root folder",

  /** string to use as an ID of sheets folder in references selector tree of Alignment Wizard   */
  sheetsFolderId = "Sheets references root folder",

  /** string to use as an ID of control points folder in references selector tree of Alignment Wizard   */
  controlPointsFolderId = "Control points references root folder",
}

/**
 * @returns true if the input id is a folder node in the tree = no associated IElement
 * @param id of the node to be tested
 */
export function isNodeFolderInClassWizardTree(id: string): boolean {
  return (
    id === ReferenceTreeFolderIds.cloudsFolderId ||
    id === ReferenceTreeFolderIds.sheetsFolderId ||
    id === ReferenceTreeFolderIds.modelFolderId
  );
}

/**
 * Check if a sheet can be used as reference for alignment other sheets.
 * At the moment those are mandatory conditions
 *  - position and scale should be defined, but rotation can be null or undefined
 *  - it should be not an overview maps coming from Orbis. For explanation see: https://faro01.atlassian.net/browse/CADBIM-1238
 *
 * @param sheet sheet to validate
 * @param store the current application state
 * @returns True if a pose of sheet is valid by for using as reference for sheet alignment.
 */
function canSheetBeUsedAsReference(
  sheet: IElementGenericImgSheet,
  store: Store,
): boolean {
  const hasValidReferenceSheetPose =
    !!sheet.pose && !!sheet.pose.pos && !!sheet.pose.scale;

  const geoSlam = selectAncestor(
    sheet,
    isIElementSectionGeoslam,
  )(store.getState());

  return hasValidReferenceSheetPose && !geoSlam;
}

/**
 * @returns control to select alignment reference element categorized by element type
 */
export function AlignmentWizardReferenceSelector(): JSX.Element {
  const dragDropManager = useDragDropManager();
  const treeRef = useRef<TreeApi<TreeData>>(undefined);
  const store = useAppStore();
  const dispatch = useAppDispatch();

  const elementToAlignId = useAppSelector(selectWizardElementToAlignId);
  const elementToAlign = useAppSelector(selectIElement(elementToAlignId));
  assert(elementToAlign, "invalid element selected for alignment");

  const referenceElementId = useAppSelector(selectWizardReferenceElementId);
  const isControlPointSheetAlignment = useAppSelector(
    selectIsControlPointSheetAlignment,
  );

  const pointClouds = useAppSelector(
    selectReferencePointClouds(elementToAlign),
  );
  const areas = useAppSelector(selectReferenceAreas(elementToAlign));

  const translate = useTranslate();

  const referenceTree = useMemo(() => {
    const referenceTree: TreeData[] = [];

    if (
      isIElementAreaSection(elementToAlign) ||
      isIElementGenericImgSheet(elementToAlign)
    ) {
      referenceTree.push({
        id: ReferenceTreeFolderIds.controlPointsFolderId,
        label: translate("Control points"),
        children: null,
      });
    }

    if (isIElementGenericImgSheet(elementToAlign)) {
      const area = selectAncestor(
        elementToAlign,
        isIElementAreaSection,
      )(store.getState());

      const sheets = selectChildrenDepthFirst(
        area,
        isIElementGenericImgSheet,
      )(store.getState());

      const sheetTreeNodes: TreeData[] = sheets
        .filter(
          (sh) =>
            sh.id !== elementToAlignId && canSheetBeUsedAsReference(sh, store),
        )
        .map((sh) => ({
          id: sh.id,
          label: sh.name,
          children: null,
          element: sh,
        }));

      if (sheetTreeNodes.length) {
        dispatch(setWizardReferenceElementId(sheetTreeNodes[0].id));
      } else {
        dispatch(resetWizardReferenceElementId());
      }

      referenceTree.push({
        id: ReferenceTreeFolderIds.sheetsFolderId,
        label: "Sheets",
        children: sheetTreeNodes.length ? sheetTreeNodes : null,
      });

      return referenceTree;
    }

    // all 3D models in the project can be used for alignment of any sheet or cloud
    const cadModels = selectAllCadModels(store.getState());

    // sort models in alphabetical order of names
    const sortedCadModels = cadModels.sort((a, b) =>
      a.name.localeCompare(b.name),
    );

    const cads: TreeData[] = isIElementModel3dStream(elementToAlign)
      ? []
      : sortedCadModels.map((cad) => ({
          id: cad.id,
          label: cad.name,
          children: null,
          element: cad,
        }));

    referenceTree.push({
      id: ReferenceTreeFolderIds.modelFolderId,
      label: translate("Models"),
      children: cads.length > 0 ? cads : null,
    });

    const areaData = areas
      ? areas.flatMap((area) => [
          {
            id: area.id,
            label: area.name,
            children: null,
            element: area,
          },
        ])
      : [];

    referenceTree.push({
      id: ReferenceTreeFolderIds.sheetsFolderId,
      label: translate("Sheets"),
      children: areaData.length > 0 ? areaData : null,
    });

    const clouds = pointClouds
      ? pointClouds.flatMap((cloud) => [
          {
            id: cloud.id,
            label: cloud.name,
            children: null,
            element: cloud,
            directParent: selectIElement(cloud.parentId)(store.getState()),
          },
        ])
      : [];

    referenceTree.push({
      id: ReferenceTreeFolderIds.cloudsFolderId,
      label: translate("Point clouds"),
      children: clouds.length > 0 ? clouds : null,
    });

    return referenceTree;
  }, [
    areas,
    dispatch,
    elementToAlign,
    elementToAlignId,
    pointClouds,
    store,
    translate,
  ]);

  const [openState, setOpenState] = useState<OpenMap>({
    [referenceTree[0]?.id]: false,
  });

  const selectNode = useCallback(
    (nodes: Array<NodeApi<TreeData>>) => {
      if (nodes.length !== 1) return;

      dispatch(
        setControlPointsAlignmentEnabled(
          nodes[0].id === ReferenceTreeFolderIds.controlPointsFolderId,
        ),
      );

      if (isNodeFolderInClassWizardTree(nodes[0].id)) return;

      if (nodes[0].id === ReferenceTreeFolderIds.controlPointsFolderId) {
        return;
      }

      const element = selectIElement(nodes[0].id)(store.getState());
      assert(element, "invalid element in reference tree");

      if (isIElementModel3dStream(element)) {
        dispatch(setActiveCad(element.id));
      }
      dispatch(setWizardReferenceElementId(element.id));
      if (isIElementGenericDataSession(element)) {
        const cloudStream = getValidReferenceCloud(element, store.getState());
        dispatch(setWizardReferencePointCloudStreamId(cloudStream?.id));
      } else {
        dispatch(setWizardReferencePointCloudStreamId(undefined));
      }
    },
    [dispatch, store],
  );

  const onToggle = useCallback(() => {
    if (treeRef.current?.openState) {
      setOpenState(treeRef.current.openState);
    }
  }, [setOpenState]);

  // selectActiveCadId is undefined if there is no CAD in the model
  const activeCadId = useAppSelector(selectActiveCadId);

  // Set the recommended item to be selected in the tree
  useEffect(() => {
    const recommendedItemId = activeCadId
      ? ReferenceTreeFolderIds.modelFolderId
      : ReferenceTreeFolderIds.controlPointsFolderId;
    dispatch(setWizardRecommendedElementId(recommendedItemId));
  }, [activeCadId, dispatch]);

  return (
    <>
      <Stack direction="row" sx={{ justifyContent: "space-between" }}>
        <FaroText variant="heading16" sx={{ paddingLeft: 3 }}>
          Align to
        </FaroText>

        <FaroTooltip title="The reference element does not move during the alignment">
          <AnchorIcon htmlColor={grey[400]} />
        </FaroTooltip>
      </Stack>

      {/* Never translate the tree as there is mostly customer data in there */}
      <NoTranslate>
        <Tree<TreeData>
          dndManager={dragDropManager}
          ref={treeRef}
          data={referenceTree}
          onSelect={selectNode}
          selection={
            isControlPointSheetAlignment
              ? ReferenceTreeFolderIds.controlPointsFolderId
              : referenceElementId
          }
          openByDefault={false}
          onToggle={onToggle}
          // Disabling dnd features for now
          disableDrag
          disableDrop
          width="100%"
          height={650}
          rowHeight={TREE_NODE_HEIGHT}
          indent={24}
          initialOpenState={openState}
        >
          {AlignmentWizardReferenceTreeNode}
        </Tree>
      </NoTranslate>
    </>
  );
}
